import React, { useContext } from 'react';
import AttributeDisplayText from '../AttributeDisplayText/AttributeDisplayText';
import { makeStyles } from 'tss-react/mui';
import EDCaptionedValue from '../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import {
  IBusinessDataFormContext,
  BusinessDataFormContext,
} from '../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { InternalError } from '../../InternalError/InternalError';
import {
  attributeDefToDataPath,
  attributeDefToValuePath,
} from '../../../Utils/dataUtils';
import { ModelContext } from '../../../Model/ModelContext/ModelContext';
import {
  getAttributeInfo,
  getValidClassesList,
} from '../../../Model/avenyModelUtils';
import { path, isEmpty, toUpper, intersperse, init, complement } from 'ramda';
import EDTypography from '../../../BaseComponents/Typography/EDTypography';
import { isArray } from '../../../Utils/ramdaCookbook';
import { AttributeValueSizeType } from '../AttributeValue/AttributeValue';

// ett attribut eller flera attribut som ska visas som en grupp (t.ex. FORNAMN EFTERNAMN)
export type AttributeGroup<T> = (keyof T & string) | Array<keyof T & string>;

interface IProps<T> {
  attributeGroups: Array<AttributeGroup<T>>;
  customDisplayLabel?: string;
  size?: AttributeValueSizeType;
  fillContainer?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}

const attributeValueUseStyles = makeStyles()(
  theme =>
    ({
      displayTextDisabled: {
        color: theme.palette.action.disabled,
      },
    } as const)
);

/**
 * Visar benämningen och värdet för ett eller flera attribut som en rubrik för ett kort
 * Används på första kortet i en objektvy
 */
function AttributeHeader<T>(props: IProps<T>) {
  const { classes, cx } = attributeValueUseStyles();

  // normalisera så det alltid är listor även för ett fält
  const attributeGroups: string[][] = props.attributeGroups.map(group =>
    isArray(group) ? group : ([group] as any)
  );

  const formContext = useContext<IBusinessDataFormContext>(
    BusinessDataFormContext
  );
  const { avenyCollectionMap } = useContext(ModelContext);

  if (formContext) {
    const attributeGroupsInfo = attributeGroups.map(attributeGroup =>
      attributeGroup.map(def => {
        const pathString = attributeDefToDataPath(def);
        const valuePath = attributeDefToValuePath(def);
        const { collection, attribute } = getAttributeInfo(
          formContext.model.CollectionName,
          def,
          avenyCollectionMap
        );
        const dataValue =
          formContext.data && (path(pathString, formContext.data) as string);
        const editedvalue =
          formContext.data &&
          (path(valuePath, formContext.formState.values) as string);
        const value = editedvalue !== undefined ? editedvalue : dataValue;
        const validTypes = (attribute.ValidInComps ?? []).flatMap(validIn =>
          getValidClassesList([collection.RootType], validIn)
        );
        const typ =
          formContext.data &&
          (path([...init(pathString), '_typ'], formContext.data) as string);
        const validType =
          typ === undefined ||
          isEmpty(validTypes) ||
          validTypes.map(toUpper).includes(typ);

        return {
          pathString,
          valuePath,
          attribute,
          value,
          validType,
        };
      })
    );

    return (
      <EDCaptionedValue
        label={
          props.customDisplayLabel ??
          attributeGroupsInfo
            .map(attrGroup =>
              attrGroup
                .filter(attr => attr.validType)
                .map(attr => attr.attribute.DisplayLabel.toUpperCase())
                .join(' ')
            )
            .filter(complement(isEmpty))
            .join(' - ')
        }
        name={attributeGroupsInfo
          .map(attrgroup =>
            attrgroup.map(attr => attr.attribute.AttributeName).join('.')
          )
          .join('-')}
        header
        noLeftMargin
        onClick={props.onClick}
        size={props.size}
      >
        <EDTypography variant="h6">
          {intersperse<React.ReactNode>(
            ' - ',
            attributeGroupsInfo
              .map(attrGroup =>
                intersperse<React.ReactNode>(
                  ' ',
                  attrGroup
                    .filter(attr => attr.validType)
                    .map((attr, idx) =>
                      attr.value ? (
                        <AttributeDisplayText
                          key={attr.attribute.AttributeName}
                          value={attr.value}
                          collection={formContext.model}
                          attributeModel={attr.attribute}
                          variant="inherit"
                          as="span"
                          classes={{
                            root: cx({
                              [classes.displayTextDisabled]: props.disabled,
                            }),
                          }}
                        />
                      ) : null
                    )
                )
              )
              .filter(complement(isEmpty))
          )}
        </EDTypography>
      </EDCaptionedValue>
    );
  } else {
    return (
      <InternalError
        id={'64930cc0-6553-4b29-93c9-7f249c69a916'}
        message="Attribut kan inte visas utanför BusinessDataFormContext"
      />
    );
  }
}

export default AttributeHeader;
